import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { PaperPlaneTilt, Robot, Image as ImageIcon, VideoCamera, X, CaretDown } from "@phosphor-icons/react";

const COMMENT_TYPES = ["Discussion", "Technical Input", "Evidence", "Use Case", "Policy", "Question"];

export default function ReplyComposer({ postId, onPosted }) {
  const [body, setBody] = useState("");
  const [commentType, setCommentType] = useState("Discussion");
  const [agentKey, setAgentKey] = useState("");
  const [agents, setAgents] = useState([]);
  const [mediaType, setMediaType] = useState("none");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/agents").then((r) => setAgents(r.data || [])).catch(() => {});
  }, []);

  const reset = () => {
    setBody("");
    setAgentKey("");
    setMediaType("none");
    setFile(null);
    setCommentType("Discussion");
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (agentKey) {
      // Agent invitation path
      setBusy(true);
      try {
        await api.post(`/posts/${postId}/replies`, {
          agent_key: agentKey,
          comment_type: commentType,
        });
        reset();
        onPosted?.();
      } catch (ex) {
        setErr(ex.response?.data?.detail || ex.message);
      } finally {
        setBusy(false);
      }
      return;
    }
    if (!body.trim() && !file) {
      setErr("Add a comment or attach media.");
      return;
    }
    setBusy(true);
    try {
      let media_url = "";
      let media_type = "none";
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        media_url = data.url;
        media_type = mediaType;
      }
      await api.post(`/posts/${postId}/replies`, {
        body,
        comment_type: commentType,
        media_type,
        media_url,
      });
      reset();
      onPosted?.();
    } catch (ex) {
      setErr(ex.response?.data?.detail || ex.message);
    } finally {
      setBusy(false);
    }
  };

  const inviting = !!agentKey;

  return (
    <form onSubmit={submit} className="border border-[#27272A] bg-[#0A0A0C] mb-8" data-testid="reply-composer">
      {/* Top selectors */}
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-[#27272A]">
        {/* Comment type */}
        <div className="relative">
          <select
            value={commentType}
            onChange={(e) => setCommentType(e.target.value)}
            className="appearance-none bg-[#121214] border border-[#27272A] pl-3 pr-8 py-1.5 text-xs font-mono uppercase tracking-wider text-white cursor-pointer"
            data-testid="reply-comment-type"
          >
            {COMMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <CaretDown size={10} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-zinc-500" />
        </div>

        {/* Agent invite */}
        <div className="relative">
          <select
            value={agentKey}
            onChange={(e) => setAgentKey(e.target.value)}
            className={`appearance-none border pl-7 pr-8 py-1.5 text-xs font-mono uppercase tracking-wider cursor-pointer ${
              agentKey
                ? "border-[#E60012] bg-[#E60012] text-white"
                : "border-[#27272A] bg-[#121214] text-zinc-300"
            }`}
            data-testid="reply-agent-invite"
          >
            <option value="">{"Invite an agent…"}</option>
            {agents.map((a) => (
              <option key={a.key} value={a.key}>
                {`${a.name} — ${a.role.replace(" Agent", "")}`}
              </option>
            ))}
          </select>
          <Robot size={11} weight="bold" className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
          <CaretDown size={10} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>

        {!inviting && (
          <>
            <label className={`btn btn-ghost btn-sm cursor-pointer ${mediaType === "image" ? "border-[#E60012] text-white" : ""}`} data-testid="reply-attach-image">
              <ImageIcon size={12} />
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => {
                const f = e.target.files?.[0] || null;
                setFile(f);
                setMediaType(f ? "image" : "none");
              }} />
            </label>
            <label className={`btn btn-ghost btn-sm cursor-pointer ${mediaType === "video" ? "border-[#E60012] text-white" : ""}`} data-testid="reply-attach-video">
              <VideoCamera size={12} />
              <input type="file" accept="video/*" className="hidden" onChange={(e) => {
                const f = e.target.files?.[0] || null;
                setFile(f);
                setMediaType(f ? "video" : "none");
              }} />
            </label>
          </>
        )}

        {file && (
          <span className="label-mono ml-1 flex items-center gap-1">
            {file.name.slice(0, 30)}{file.name.length > 30 ? "…" : ""}
            <button type="button" onClick={() => { setFile(null); setMediaType("none"); }} className="text-zinc-500 hover:text-white">
              <X size={12} />
            </button>
          </span>
        )}
      </div>

      {/* Body */}
      {inviting ? (
        <div className="p-4 text-sm text-zinc-300 bg-[#121214]">
          <Robot size={12} weight="bold" className="inline mr-1.5 text-[#E60012]" />
          You're inviting <span className="text-white font-medium">{agents.find((a) => a.key === agentKey)?.name}</span> to respond.
          They will write a professional reply based on the post.
        </div>
      ) : (
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          placeholder="Add evidence, ask a question, or share field experience…"
          className="w-full bg-transparent text-white p-4 text-sm focus:outline-none resize-none placeholder:text-zinc-500"
          data-testid="reply-input"
        />
      )}

      {err && <div className="mx-4 mb-3 text-xs text-red-500" data-testid="reply-error">{err}</div>}

      <div className="flex items-center justify-between px-3 py-2 border-t border-[#27272A]">
        <span className="label-mono">
          {inviting
            ? `INVITING ${agents.find((a) => a.key === agentKey)?.name?.toUpperCase()}`
            : "REPLIES EARN 5 XP · +3 FOR MEDIA"}
        </span>
        <button type="submit" disabled={busy} className="btn btn-primary btn-sm" data-testid="reply-submit-btn">
          {busy ? <span className="spinner" /> : <>{inviting ? "Invoke agent" : "Post"} <PaperPlaneTilt size={11} weight="bold" /></>}
        </button>
      </div>
    </form>
  );
}
